import { useTranslations } from 'next-intl';
import { type FC, useEffect, useMemo, useRef } from 'react';
import { BaseLineCheck3 } from '@/app/components/icons/BaseLineCheck3';
import { ProductLinePic } from '@/app/components/icons/ProductLinePic';
import { ProductLineView } from '@/app/components/icons/ProductLineView';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import HoverPlayMedia from '@/app/components/shared/HoverPlayMedia';
import { MyCreationsSelector } from '@/app/components/shared/MyCreationsSelector';
import { Button } from '@/app/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/app/components/ui/dialog';
import { useDevice, useResolution } from '@/app/hooks';
import type { CreationWithTask } from '@/app/types';
import { cn, formatDurationText, isExtendTask, isVideoDurationWithinRange } from '@/app/utils';

interface CreationSelectorDialogBaseProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mediaType: 'video' | 'image';
  /** 覆盖作品列表请求类型；不传则使用 `[mediaType]` */
  creationTypes?: ('video' | 'image')[];
  /** 覆盖弹窗标题；不传则使用 `tools.target*` 文案 */
  pickerTitle?: string;
  maxDuration?: number;
  minDuration?: number;
  isCreationDisabled?: (creation: CreationWithTask) => boolean;
  getDisabledReason?: (creation: CreationWithTask) => string | null;
  confirmLoading?: boolean;
  onConfirm: () => void;
  onViewCreation: (creation: CreationWithTask) => void;
}

export interface CreationSelectorDialogSingleProps extends CreationSelectorDialogBaseProps {
  multiple?: false;
  selectedIndex: number | null;
  onSelectionChange: (index: number | null, creation?: CreationWithTask) => void;
}

export interface CreationSelectorDialogMultiProps extends CreationSelectorDialogBaseProps {
  multiple: true;
  selectedIndices: number[];
  onMultiSelectionChange: (indices: number[], creations: CreationWithTask[]) => void;
  maxMultiSelect?: number;
  onMultiSelectLimitReached?: () => void;
}

export type CreationSelectorDialogProps = CreationSelectorDialogSingleProps | CreationSelectorDialogMultiProps;

export const CreationSelectorDialog: FC<CreationSelectorDialogProps> = (props) => {
  const {
    open,
    onOpenChange,
    mediaType,
    creationTypes,
    pickerTitle,
    maxDuration,
    minDuration,
    isCreationDisabled,
    getDisabledReason,
    confirmLoading,
    onConfirm,
    onViewCreation,
  } = props;

  const multiple = props.multiple === true;
  const onSelectionChange = !multiple ? props.onSelectionChange : undefined;
  const onMultiSelectionChange = multiple ? props.onMultiSelectionChange : undefined;
  const maxMultiSelect = multiple ? props.maxMultiSelect : undefined;
  const onMultiSelectLimitReached = multiple ? props.onMultiSelectLimitReached : undefined;

  const t = useTranslations();
  const { resolutionMap } = useResolution();
  const { isMobile } = useDevice();
  const wasOpenRef = useRef(open);
  const columnCount = isMobile ? 2 : 4;
  const rowHeight = isMobile ? 120 : 200;
  const gap = isMobile ? 12 : 16;

  useEffect(() => {
    if (wasOpenRef.current && !open) {
      if (multiple) {
        onMultiSelectionChange?.([], []);
      } else {
        onSelectionChange?.(null);
      }
    }
    wasOpenRef.current = open;
  }, [open, multiple, onMultiSelectionChange, onSelectionChange]);

  const isCreationDurationOutOfRange = (creation: CreationWithTask): boolean => {
    if (creation.type === 'video') {
      const duration = creation.duration ?? creation.task.settings.duration ?? 0;
      return !isVideoDurationWithinRange({
        duration,
        minDuration,
        maxDuration,
      });
    }
    return false;
  };

  const isCreationUnavailable = (creation: CreationWithTask): boolean => {
    return isCreationDurationOutOfRange(creation) || isCreationDisabled?.(creation) === true;
  };

  const selectorTypes = useMemo(
    () => (creationTypes?.length ? creationTypes : [mediaType]) as ('video' | 'image')[],
    [creationTypes, mediaType],
  );

  const selectorShared = {
    types: selectorTypes,
    columnCount,
    rowHeight,
    gap,
    pageSize: 30,
    isItemDisabled: isCreationUnavailable,
    getItemKey: (creation: CreationWithTask) => creation.id,
    itemClassName: cn('grid-cols-4', isMobile && 'grid-cols-2 gap-3'),
    rowItemClassName: 'aspect-[unset]',
    renderItem: (creation: CreationWithTask, _index: number, isSelected: boolean) => {
      const isDisabled = isCreationUnavailable(creation);
      const disabledReason = isDisabled ? getDisabledReason?.(creation) : null;
      const displayDuration = isExtendTask(creation.task.type) ? creation.duration : creation.task.settings.duration;

      return (
        <HoverPlayMedia
          id={creation.id}
          uri={creation.uri}
          coverUri={creation.cover_uri}
          eventName="submit"
          className="size-full"
          imageClassName={cn(
            'left-0 right-0 w-full h-full -z-[1] object-cover object-center',
            isSelected && 'absolute',
          )}
          videoClassName={cn('left-0 right-0 w-full h-full -z-[1] object-cover', isSelected && 'absolute')}
        >
          <div className="absolute right-2 bottom-2 left-2 z-[10] flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 rounded-6 bg-black bg-opacity-[.2] px-2 py-0.5 font-normal text-white text-xs leading-[1.4] backdrop-blur-[5px]">
              {creation.type === 'video' && formatDurationText(displayDuration, t)}
              {creation.type === 'image' && <ProductLinePic className="size-4 flex-shrink-0" />}
              <div className="h-[9px] w-[1px] bg-white bg-opacity-[.16]" />
              <CustomTooltip
                content={resolutionMap[creation?.resolution?.resolution as keyof typeof resolutionMap]}
                showOnlyOnOverflow
                trigger={isMobile ? 'click' : 'hover'}
                maxLines={1}
              >
                {resolutionMap[creation?.resolution?.resolution as keyof typeof resolutionMap]}
              </CustomTooltip>
            </div>
            <div
              className="rounded-6 bg-black bg-opacity-[.2] px-1 py-0.5 backdrop-blur-[5px]"
              onClick={() => onViewCreation(creation)}
            >
              <ProductLineView className="size-4" />
            </div>
          </div>
          {disabledReason ? (
            <div className="absolute inset-x-2 top-2 z-[11] rounded-6 bg-black/60 px-2 py-1 text-center font-medium text-[11px] text-white backdrop-blur-[5px]">
              {disabledReason}
            </div>
          ) : null}
          <div className="absolute bottom-0 left-0 h-10 w-full bg-gradient-to-t from-black p-2" />
          <BaseLineCheck3
            className={cn('absolute top-1 left-1 z-[1] hidden text-2xl', {
              inline: isSelected && isMobile,
            })}
          />
        </HoverPlayMedia>
      );
    },
  };

  const confirmDisabled = confirmLoading || (multiple && props.selectedIndices.length === 0);

  const creationsSelector = multiple ? (
    <MyCreationsSelector
      {...selectorShared}
      multiple
      selectedIndices={props.selectedIndices}
      onMultiSelectionChange={props.onMultiSelectionChange}
      maxMultiSelect={maxMultiSelect}
      onMultiSelectLimitReached={onMultiSelectLimitReached}
    />
  ) : (
    <MyCreationsSelector
      {...selectorShared}
      selectedIndex={props.selectedIndex}
      onSelectionChange={props.onSelectionChange}
    />
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl sm:max-w-[calc(100vw-24px)]">
        <DialogHeader>
          <DialogTitle>
            {pickerTitle ?? (mediaType === 'video' ? t('tools.targetVideo') : t('tools.targetImage'))}
          </DialogTitle>
        </DialogHeader>
        <div className="hidden-scrollbar h-[70vh] overflow-auto sm:h-[60vh]">{creationsSelector}</div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.dialog.cancel')}
          </Button>
          <Button variant="primary" onClick={onConfirm} disabled={confirmDisabled} loading={confirmLoading}>
            {t('common.dialog.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
